import client from "Libs/platform";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { selectCurrentUserIsInternal } from "Reducers/app/selectors";
import { organizationByNameOrIdSelector } from "Reducers/organization/organization";
import { getOrganizationTrial } from "Reducers/organization/trial";

import { projectCreationActions } from "../projectCreation.reducer";
import { verificationTypes } from "../projectCreation.types";

import type { VerificationType } from "../projectCreation.types";

export type FetchVerificationTypeProps = {
  organizationId: string;
  isRefreshing?: boolean;
};

export type FetchVerificationTypeResult = {
  type: VerificationType;
  credentials: {
    client_secret?: string;
    public_key?: string;
    automatic_payment_methods?: boolean;
  } | null;
  isOnTrial: boolean;
  canCreate: boolean;
  message?: string;
};

/**
 * Fetches the verification type required for an organization.
 *
 * This thunk determines what verification (if any) is required before
 * a user can create a project. It checks:
 * - If user is internal (staff) - no verification needed
 * - If user needs full billing details
 * - If user just needs credit card
 * - If user can proceed without verification
 */
export const fetchVerificationType = createAppAsyncThunk(
  "onboarding/fetchVerificationType",
  async (
    { organizationId, isRefreshing = false }: FetchVerificationTypeProps,
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const isInternalUser = selectCurrentUserIsInternal(state);

      let isOnTrial = false;

      // Get organization trial to check trial status (skip if just refreshing).
      // Callers pass the org UUID, but getOrganizationTrial expects a description
      // name (it does a name -> id lookup internally), so resolve here.
      //
      // The dispatch is also what fills the trial slice `useTrialInfo` reads, so
      // it stays whether or not anyone wants the returned flag.
      if (!isRefreshing) {
        const organization = organizationByNameOrIdSelector(state, {
          organizationNameOrId: organizationId
        });
        if (organization?.name) {
          const trialResult = await dispatch(
            getOrganizationTrial({ organizationId: organization.name })
          ).unwrap();
          isOnTrial = trialResult?.status === "active";
        }
      }

      // Check what's required to create a subscription
      const { required_action, can_create, message } =
        await client.getOrganizationSubscriptionCanCreate(organizationId);

      let verificationType: VerificationType = verificationTypes.default;

      // Determine verification type based on user and organization state
      if (isInternalUser) {
        verificationType = verificationTypes.none;
      } else if (required_action?.action === "billing_details") {
        verificationType = verificationTypes.full;
      } else if (required_action?.type === "credit-card") {
        verificationType = verificationTypes.creditCard;
      } else if (required_action?.action === "retry") {
        verificationType = verificationTypes.idle;
      } else if (can_create) {
        verificationType = verificationTypes.none;
      }

      dispatch(
        projectCreationActions.setVerificationType({
          organizationId,
          verificationType
        })
      );

      if (required_action?.credentials) {
        dispatch(
          projectCreationActions.setVerificationCredentials({
            organizationId,
            credentials: required_action.credentials
          })
        );
      }

      return {
        type: verificationType,
        credentials: required_action?.credentials ?? null,
        isOnTrial,
        canCreate: can_create,
        message
      };
    } catch (err: unknown) {
      const error = err instanceof Error ? err.message : "An error occurred";

      logger(err, {
        action: "fetchVerificationType",
        organizationId
      });

      // Return idle state on error so user can retry
      dispatch(
        projectCreationActions.setVerificationType({
          organizationId,
          verificationType: verificationTypes.idle
        })
      );

      return rejectWithValue({ error });
    }
  }
);
